import api from '../axios.config';

/**
 * Who in this organisation sees a customer whole.
 *
 * Distinct from the masking the browser already does on a few screens: that
 * draws asterisks over a value the API has already sent in full. This policy
 * decides what the API sends, so the masked value is what crosses the wire.
 */

export interface PiiPolicy {
  organizationId: string;
  enabled: boolean;
  /** Comma-separated role names the policy applies to. */
  maskRoles: string;
  maskPhone: boolean;
  maskEmail: boolean;
  maskName: boolean;
  restrictToAssigned: boolean;
}

export interface PolicyPreview {
  hasSample: boolean;
  before?: { phone: string | null; displayName: string | null; fields: Record<string, unknown> };
  after?: { phone: string | null; displayName: string | null; fields: Record<string, unknown> };
}

/**
 * The roles worth offering.
 *
 * Only the ones a business would plausibly restrict. Platform and provider
 * roles are deliberately absent: masking the provider who administers the
 * tenant is not a control a business needs, and offering it would invite
 * somebody to lock the people who can help them out of the data.
 */
export const MASKABLE_ROLES: Array<{ value: string; label: string; help: string }> = [
  {
    value: 'BUSINESS_AGENT',
    label: 'Agents',
    help: 'Floor staff who handle conversations as they arrive.',
  },
  {
    value: 'BUSINESS_USER',
    label: 'Users',
    help: 'Everyday staff who work leads but do not administer the account.',
  },
  {
    value: 'BUSINESS_ADMIN',
    label: 'Admins',
    help: 'Your own administrators. Also covers the legacy Operator Admin role.',
  },
];

const unwrap = <T,>(res: any): T | null =>
  res?.data?.success && res.data.data !== undefined ? (res.data.data as T) : null;

const fail = (e: any, fallback: string) => ({
  ok: false as const,
  error: e?.response?.data?.message || e?.response?.data?.error || fallback,
});

export const privacyService = {
  async get(): Promise<PiiPolicy | null> {
    try {
      return unwrap<PiiPolicy>(await api.get('/v1/privacy/policy'));
    } catch {
      return null;
    }
  },

  async save(patch: Partial<PiiPolicy>): Promise<
    { ok: true; data: PiiPolicy } | { ok: false; error: string }
  > {
    try {
      const res = await api.put('/v1/privacy/policy', patch);
      const data = unwrap<PiiPolicy>(res);
      return data ? { ok: true, data } : { ok: false, error: res?.data?.message || 'Could not save.' };
    } catch (e: any) {
      return fail(e, 'Could not save.');
    }
  },

  /**
   * A real capture from this organisation, before and after.
   *
   * Real rather than invented on purpose: a made-up example always masks
   * cleanly, and the surprises live in this organisation's own captured fields.
   */
  async preview(opts: { maskPhone: boolean; maskEmail: boolean; maskName: boolean }): Promise<PolicyPreview> {
    try {
      const res = await api.get('/v1/privacy/policy/preview', { params: opts });
      return unwrap<PolicyPreview>(res) ?? { hasSample: false };
    } catch {
      return { hasSample: false };
    }
  },
};

/** The roles named by a policy, as a set the checkboxes can read. */
export const parseRoles = (raw: string | undefined): string[] =>
  (raw || '')
    .split(',')
    .map((r) => r.trim().toUpperCase())
    .filter(Boolean);
